import React from 'react';
import { SmoothHouseIcon } from '@components/icons/smooth-house-icon';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { EngineIcon, ENGINE_KINDS, ENGINE_ORDER, type EngineKey } from './engines';
import { FamilyGroup, type Family } from './figures';
import { clamp, draw, ease, enter, pop, tween } from './timeline';

/*
 * The wish story's scenes, drawn on a fixed 1600 × 900 stage that the
 * story scales and frames (see `wish-story.tsx`). Positions are the
 * design lab's; narrow screens reflow the few pieces that span the stage.
 */

export const W = 1600;
export const H = 900;
export const GROUND = 790;

/** Three across, or one column on a phone. */
export type Layout = 'wide' | 'tall';

/** Which side of a mom something stands: the side she faces. Maya faces
 *  left as she walks in from the right; Jess, mirrored, faces right. */
export type Side = 'left' | 'right';

export const WISH = 'Help me get the kids out of the house.';
const GENERIC = 'Try the library or a park.';
const NASA = 'The new Ames NASA Visitor Center opened this week.';
const NASA_CHIPS = ['Sat 10:00', 'Kids’ launch lab', '22 min drive'] as const;

const C = {
  ink: colors.neutral.extraDark,
  body: colors.neutral.darkMedium,
  muted: colors.neutral.medium,
  subtle: colors.neutral.mediumLight,
  line: colors.neutral.extraLight,
  bubble: colors.brand.primaryLight,
  brandD: colors.brand.primaryDark,
  /* Seafoam deep enough for small text on white (6.6:1). */
  teal: colors.brand.exxxtraDark,
  coral: colors.accent.coral.extraDark,
  white: colors.white,
} as const;

const PF = typography.fontFamily.body;
/* The site's face for small labels and metadata. */
const SF = typography.fontFamily.small;

/* The site's primary CTA. */
const CORAL_CTA = `linear-gradient(135deg, ${colors.accent.coral.extraDark}, ${colors.accent.coral.exxtraDark})`;

/* A member's message as the app draws it: coral glass with ink text. */
const WISH_BUBBLE: React.CSSProperties = {
  background: `linear-gradient(180deg, ${colors.chat.userGlassTop}, ${colors.chat.userGlassBottom})`,
  border: `1px solid ${colors.chat.glassRim}`,
  color: C.ink,
};

/** The app's icon: the brush house on seafoam. */
export const AppMark: React.FC<{ size: number; radius: number; style?: React.CSSProperties }> = ({ size, radius, style }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: radius,
      background: `radial-gradient(circle at 50% 42%, ${colors.brand.extraLight} 0%, ${colors.brand.primary} 62%, ${colors.brand.primaryDark} 100%)`,
      color: colors.highlight.dark,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...style,
    }}
  >
    <SmoothHouseIcon size={size * 0.72} />
  </div>
);

const Check: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const Chip: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <span
    style={{
      font: `600 17px ${SF}`,
      background: C.white,
      color: C.body,
      padding: '4px 9px',
      borderRadius: 9999,
      boxShadow: `0 1px 3px ${colors.darkOverlay10}`,
      ...style,
    }}
  >
    {children}
  </span>
);

// ── A mom's line ─────────────────────────────────────────────────────────────

type SpeechProps = {
  T: number;
  at: number;
  until: number;
  /** Where the mom stands; the bubble opens toward the side she faces. */
  x: number;
  side: Side;
  layout: Layout;
  children: React.ReactNode;
};

export const Speech: React.FC<SpeechProps> = ({ T, at, until, x, side, layout, children }) => {
  const inn = pop(T, at, 0.55);
  const out = draw(T, until - 0.4, 0.4);
  /* On a phone, narrow enough to clear the frame's faded edge. */
  const width = layout === 'tall' ? 460 : 620;
  const right = side === 'right';
  return (
    <div
      style={{
        position: 'absolute',
        left: right ? x - 40 : x + 40 - width,
        bottom: H - GROUND + 322,
        width,
        transformOrigin: right ? '10% 100%' : '90% 100%',
        opacity: Number(inn.opacity) * (1 - out),
        transform: `${inn.transform} scale(${1 - out * 0.1})`,
      }}
    >
      <div
        style={{
          background: C.white,
          borderRadius: 28,
          boxShadow: `0 14px 28px ${colors.shadowSoft}`,
          padding: '20px 28px',
          font: `400 27px/36px ${PF}`,
          /* No lone last word on a phone's narrower bubble. */
          textWrap: 'pretty',
          color: C.ink,
        }}
      >
        {children}
      </div>
      {/* The tail leans back toward her face. */}
      <div
        style={{
          position: 'absolute',
          [right ? 'left' : 'right']: 52,
          bottom: -16,
          width: 0,
          height: 0,
          borderLeft: `${right ? 10 : 18}px solid transparent`,
          borderRight: `${right ? 18 : 10}px solid transparent`,
          borderTop: `20px solid ${C.white}`,
        }}
      />
    </div>
  );
};

// ── The phone, close up ──────────────────────────────────────────────────────

const DOT_KEYS = ['a', 'b', 'c'] as const;

const Dots: React.FC<{ T: number; at: number; until: number }> = ({ T, at, until }) => {
  if (T < at || T >= until) return null;
  return (
    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', height: 24, padding: '0 14px', background: C.bubble, borderRadius: 16 }}>
      {DOT_KEYS.map((key, i) => (
        <div
          key={key}
          style={{ width: 8, height: 8, borderRadius: '50%', background: C.subtle, opacity: 0.4 + 0.6 * Math.abs(Math.sin(T * 6 + i * 1.1)) }}
        />
      ))}
    </div>
  );
};

type PhoneProps = {
  T: number;
  at: number;
  until: number;
  typeAt: number;
  typeDur: number;
  replyAt: number;
  /** 1: today's generic answer. 2: the answer built from the last wish. */
  version: 1 | 2;
  tapAt?: number;
  /** The side of her it stands on — the side she faces. */
  side: Side;
};

/** Where the phone stands: on the side the mom faces, grown out of her
 *  hand — right of Jess at 520, and mirrored, left of Maya at 1080. */
export const PHONE_BOX = { width: 380, top: 170, height: 560, inset: 820 } as const;

/** Her raised hand, where the phone grows from. */
const HAND_Y = 630;
export const phoneLeft = (side: Side) => (side === 'right' ? PHONE_BOX.inset : W - PHONE_BOX.inset - PHONE_BOX.width);

export const Phone: React.FC<PhoneProps> = ({ T, at, until, typeAt, typeDur, replyAt, version, tapAt = Infinity, side }) => {
  const inn = tween(T, 0, 1, at, at + 0.6, ease.outBack);
  const out = draw(T, until - 0.5, 0.5);
  const typed = WISH.slice(0, Math.round(draw(T, typeAt, typeDur, ease.linear) * WISH.length));
  const special = version === 2;
  const added = special && T >= tapAt;
  const ripple = special ? draw(T, tapAt, 0.6) : 0;
  const finger = special ? draw(T, tapAt - 0.5, 0.45) * (1 - draw(T, tapAt + 0.15, 0.3)) : 0;
  const pressed = T > tapAt - 0.08 && T < tapAt + 0.12;
  const bubbleFont = `400 18px/25px ${PF}`;
  return (
    <div
      style={{
        position: 'absolute',
        left: phoneLeft(side),
        top: PHONE_BOX.top,
        width: PHONE_BOX.width,
        height: PHONE_BOX.height,
        borderRadius: 44,
        background: C.ink,
        padding: 10,
        boxSizing: 'border-box',
        boxShadow: `0 30px 60px ${colors.shadowHard}`,
        /* Out of her raised hand, 220 beyond the phone's near edge. */
        transformOrigin: `${side === 'right' ? -220 : PHONE_BOX.width + 220}px ${HAND_Y - PHONE_BOX.top}px`,
        transform: `scale(${inn * (1 - out)})`,
        opacity: clamp(inn * 3, 0, 1) * (1 - out),
      }}
    >
      <div style={{ width: '100%', height: '100%', borderRadius: 36, background: C.white, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '22px 20px 12px', borderBottom: `1px solid ${C.line}` }}>
          <AppMark size={30} radius={8} />
          <span style={{ font: `600 18px ${PF}`, color: C.ink }}>The Home CEO</span>
          <span style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: colors.brand.exxtraDark }} />
        </div>
        <div style={{ flex: 1, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              alignSelf: 'flex-end',
              maxWidth: 270,
              ...WISH_BUBBLE,
              borderRadius: '18px 18px 4px 18px',
              padding: '10px 14px',
              font: bubbleFont,
              minHeight: 45,
              boxSizing: 'border-box',
              opacity: T >= typeAt ? 1 : 0,
            }}
          >
            {typed}
            <span
              style={{
                display: typed.length < WISH.length ? 'inline-block' : 'none',
                width: 2,
                height: 18,
                background: C.ink,
                marginLeft: 2,
                verticalAlign: -3,
              }}
            />
          </div>
          <div style={{ alignSelf: 'flex-start' }}>
            <Dots T={T} at={typeAt + typeDur + 0.2} until={replyAt} />
          </div>
          {special ? (
            <div
              style={{
                alignSelf: 'flex-start',
                width: 320,
                background: C.bubble,
                borderRadius: '18px 18px 18px 4px',
                padding: '12px 14px 14px',
                boxSizing: 'border-box',
                ...enter(T, replyAt, 0.5, 10),
              }}
            >
              <div style={{ font: `500 18px/24px ${PF}`, color: C.ink }}>{NASA}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {NASA_CHIPS.map((chip, i) => (
                  <Chip key={chip} style={enter(T, replyAt + 0.4 + i * 0.15, 0.4, 6)}>
                    {chip}
                  </Chip>
                ))}
              </div>
              <div
                style={{
                  position: 'relative',
                  marginTop: 12,
                  height: 44,
                  borderRadius: 9999,
                  background: added ? C.white : CORAL_CTA,
                  color: added ? C.teal : C.white,
                  font: `600 17px ${PF}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: added ? `inset 0 0 0 2px ${C.brandD}` : colors.shadows.coralGlow,
                  ...enter(T, replyAt + 0.8, 0.5, 8),
                  transform: `scale(${pressed ? 0.95 : 1})`,
                }}
              >
                {added && <Check size={18} />}
                {added ? 'Added to Saturday' : 'Add to Saturday'}
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 44,
                    height: 44,
                    margin: '-22px 0 0 -22px',
                    borderRadius: '50%',
                    background: colors.darkOverlay20,
                    transform: `scale(${finger})`,
                    opacity: finger,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 44,
                    height: 44,
                    margin: '-22px 0 0 -22px',
                    borderRadius: '50%',
                    border: `2px solid ${C.coral}`,
                    transform: `scale(${1 + ripple * 2.6})`,
                    opacity: ripple > 0 ? 1 - ripple : 0,
                  }}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                alignSelf: 'flex-start',
                maxWidth: 280,
                background: C.bubble,
                borderRadius: '18px 18px 18px 4px',
                padding: '10px 14px',
                font: bubbleFont,
                color: C.ink,
                ...enter(T, replyAt, 0.5, 10),
              }}
            >
              {GENERIC}
            </div>
          )}
        </div>
        <div
          style={{
            margin: '0 16px 18px',
            height: 44,
            borderRadius: 9999,
            background: C.bubble,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            font: `400 17px ${PF}`,
            color: C.muted,
          }}
        >
          Make a wish…
        </div>
      </div>
    </div>
  );
};

// ── Night: the wish lifts out of the day, and the assistant wakes ────────────

export const Night: React.FC<{ T: number; start: number; endAt: number; layout: Layout }> = ({ T, start, endAt, layout }) => {
  /* On a phone the card keeps a margin inside the camera's shot. */
  const cardW = layout === 'tall' ? 560 : 600;
  const rise = draw(T, start + 1.9, 1.1);
  const top = 470 - (470 - 112) * rise;
  const lift = 1 - draw(T, start + 0.3, 0.9, ease.outCubic);
  /* The mark rises to where the next scene's ring will stand. */
  const logoTop = 760 - (760 - 330) * draw(T, start + 2.7, 1.2);
  const out = 1 - draw(T, endAt - 0.2, 0.2);
  const bubbleFont = `400 21px/29px ${PF}`;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div
        style={{
          position: 'absolute',
          left: 800 - cardW / 2,
          top,
          width: cardW,
          borderRadius: 24,
          background: C.white,
          boxShadow: `0 24px 60px ${colors.darkOverlay30}`,
          padding: '18px 22px 20px',
          boxSizing: 'border-box',
          transformOrigin: '50% 0%',
          transform: `scale(${1 - 0.06 * rise}) translateY(${lift * 60}px)`,
          opacity: draw(T, start + 0.3, 0.5) * out,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            font: `600 18px ${SF}`,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: C.muted,
          }}
        >
          <span>Saturday · 9:12 am · Maya</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {/* The wish alone: what the night reviews (June, 2026-09-23). */}
          <div style={{ alignSelf: 'flex-end', ...WISH_BUBBLE, borderRadius: '16px 16px 4px 16px', padding: '8px 14px', font: bubbleFont }}>{WISH}</div>
        </div>
      </div>
      <AppMark
        size={96}
        radius={24}
        style={{
          position: 'absolute',
          left: 800 - 48,
          top: logoTop - 48,
          boxShadow: `0 14px 32px ${colors.darkOverlay40}`,
          opacity: draw(T, start + 2.7, 0.5) * out,
        }}
      />
    </div>
  );
};

// ── Learn: what would have made it better — three engines attach ─────────────

/* What each engine adds (June, 2026-09-23), and the colour of its words on
   the night: zen's light end, which reads there as large text — 3.5:1 or
   better on the night's lightest water, 4.0:1 on its median. */
export const ADDS: Record<EngineKey, { add: string; ink: string }> = {
  data: { add: '100k+ Family Friendly Attractions and Local Events', ink: colors.accent.zen.extraLight },
  expert: { add: 'Evaluation of Physical Play Areas and Inspirational Memory Development', ink: colors.accent.zen.light },
  skills: { add: 'Drive Times and Full Day Schedules in Calendar', ink: colors.accent.zen.light },
};

/* Zen's dark end is darker than the night it's drawn on: every engine's
   line and arc rides on a rim of zen's light, so the dark one shows. */
const RIM = colors.accent.zen.light;

/* The engines in the order it builds them — the data, the expertise, the
   capability — attaching around the ring from its lower left. */
const ENGINES = ENGINE_ORDER.map((key, i) => ({ key, ...ENGINE_KINDS[key], ...ADDS[key], angle: 150 - 60 * i }));

type Engine = (typeof ENGINES)[number];

/** A kind's name in block letters, highlighted in its colour — as the demo
 *  cards show it. Narrow, it takes two lines. */
const KindPill: React.FC<{ e: Engine; size: number; narrow?: boolean }> = ({ e, size, narrow = false }) => (
  <span
    style={{
      display: 'inline-block',
      maxWidth: narrow ? 188 : undefined,
      padding: `${Math.round(size * 0.3)}px ${Math.round(size * 0.7)}px`,
      borderRadius: narrow ? 14 : 9999,
      background: e.pill,
      color: e.pillInk,
      font: `700 ${size}px/1.2 ${SF}`,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      whiteSpace: narrow ? 'normal' : 'nowrap',
      boxShadow: `0 0 ${Math.round(size * 1.2)}px ${RIM}55`,
    }}
  >
    {e.label}
  </span>
);

const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => [
  cx + r * Math.cos((deg * Math.PI) / 180),
  cy + r * Math.sin((deg * Math.PI) / 180),
];

const arcPath = (cx: number, cy: number, r: number, a0: number, a1: number) => {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  return `M${x0} ${y0} A${r} ${r} 0 0 1 ${x1} ${y1}`;
};

/** How long each engine is read, large, before it settles. */
const READ = 3.4;

export const Learn: React.FC<{ T: number; start: number; endAt: number; layout: Layout }> = ({ T, start, endAt, layout }) => {
  const tall = layout === 'tall';
  const out = 1 - draw(T, endAt - 0.9, 0.7);
  const cx = 800;
  const cy = 330;
  const r = 100;
  const ny = tall ? 660 : 625;
  /* Each engine comes in oversized, in the middle, to be read (June,
     2026-09-23), then settles into its place under the diagram. */
  const comeAt = ENGINES.map((_, i) => start + 0.9 + i * 4.2);
  const settleAt = comeAt.map((at) => at + READ);
  const reading = comeAt.map((at, i) => draw(T, at, 0.5, ease.outCubic) * (1 - draw(T, settleAt[i] + 0.35, 0.45)));
  /* While an engine is read, it has the stage: the diagram behind it
     steps back. */
  const hush = Math.max(...reading);
  const behind = 1 - 0.7 * hush;
  const doneAt = settleAt[2] + 1.9;
  const done = draw(T, doneAt, 0.6);
  const pulse = 1 + 0.06 * done * Math.abs(Math.sin((T - doneAt) * 3));
  const nodeX = (i: number) => (tall ? cx + (i - 1) * 190 : 440 + i * 360);
  const column = tall ? { left: 510, width: 580 } : { left: 300, width: 1000 };
  /* Read large over the diagram; settled small under each engine. */
  const readTop = tall ? 440 : 386;
  const slotTop = ny + 50;
  const readSize = tall ? 34 : 46;
  const slotSize = 18;
  return (
    <div style={{ position: 'absolute', inset: 0, opacity: out }}>
      {/* The wish under review, as the night's card held it. */}
      <div style={{ position: 'absolute', ...column, top: tall ? 96 : 120, textAlign: 'center', ...enter(T, start + 0.05, 0.5, 0) }}>
        <div style={{ font: `300 30px/40px ${PF}`, color: C.white, opacity: 1 - 0.4 * hush }}>“{WISH}”</div>
      </div>
      <svg width={W} height={H} style={{ position: 'absolute', inset: 0, opacity: behind }} fill="none">
        <circle cx={cx} cy={cy} r={r} fill={colors.overlayWhite10} stroke={colors.overlayDegrees15} strokeWidth="4" />
        {ENGINES.map((e, i) => {
          const [px, py] = polar(cx, cy, r, e.angle);
          const line = draw(T, settleAt[i] + 0.7, 0.7);
          const arc = draw(T, settleAt[i] + 1.0, 0.8);
          return (
            <g key={e.key}>
              {[
                { stroke: RIM, width: 7, opacity: 0.45 },
                { stroke: e.light, width: 3, opacity: 0.9 },
              ].map((pen) => (
                <path
                  key={pen.width}
                  d={`M${nodeX(i)} ${ny - 36} L${px} ${py}`}
                  stroke={pen.stroke}
                  strokeWidth={pen.width}
                  strokeLinecap="round"
                  pathLength={1}
                  strokeDasharray={`${line} 1`}
                  opacity={line > 0 ? pen.opacity : 0}
                />
              ))}
              {[
                { stroke: RIM, width: 12, opacity: 0.45 },
                { stroke: e.light, width: 8, opacity: 1 },
              ].map((pen) => (
                <path
                  key={pen.width}
                  d={arcPath(cx, cy, r, e.angle - 54, e.angle + 54)}
                  stroke={pen.stroke}
                  strokeWidth={pen.width}
                  strokeLinecap="round"
                  pathLength={1}
                  strokeDasharray={`${arc} 1`}
                  opacity={arc > 0 ? pen.opacity : 0}
                />
              ))}
            </g>
          );
        })}
      </svg>
      {/* The Home CEO, named inside its own ring, under its mark. */}
      <div
        style={{
          position: 'absolute',
          left: cx - 90,
          top: cy - 50,
          width: 180,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          transform: `scale(${pulse})`,
          opacity: behind,
        }}
      >
        <AppMark size={64} radius={16} style={{ boxShadow: `0 10px 24px ${colors.darkOverlay40}` }} />
        <div style={{ font: `600 19px/24px ${PF}`, color: C.white, whiteSpace: 'nowrap', ...enter(T, start + 0.4, 0.5, 6) }}>
          The Home CEO
        </div>
      </div>
      {ENGINES.map((e, i) => {
        const nx = nodeX(i);
        /* Read large, then carried down to the engine's place, shrinking,
           as its settled label comes up there. */
        const fly = draw(T, settleAt[i], 0.8, ease.inOutCubic);
        const grow = draw(T, comeAt[i], 0.5, ease.outCubic);
        const settled = draw(T, settleAt[i] + 0.5, 0.4);
        const node = pop(T, settleAt[i] + 0.4, 0.6);
        return (
          <div key={e.key}>
            {reading[i] > 0 && (
              <div
                style={{
                  position: 'absolute',
                  ...column,
                  top: readTop,
                  textAlign: 'center',
                  transformOrigin: '50% 0',
                  transform: `translate(${(nx - cx) * fly}px, ${(slotTop - readTop) * fly}px) scale(${(0.94 + 0.06 * grow) * (1 - (1 - slotSize / readSize) * fly)})`,
                  opacity: reading[i],
                  zIndex: 1,
                }}
              >
                <KindPill e={e} size={readSize} />
                <div
                  style={{
                    maxWidth: tall ? 560 : 900,
                    margin: `${tall ? 12 : 16}px auto 0`,
                    font: `500 ${tall ? 28 : 36}px/1.25 ${PF}`,
                    color: e.ink,
                    textWrap: 'balance',
                  }}
                >
                  {e.add}
                </div>
              </div>
            )}
            <div
              style={{
                position: 'absolute',
                left: nx - 36,
                top: ny - 36,
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: e.fill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 28px ${RIM}66`,
                ...node,
                opacity: Number(node.opacity) * behind,
              }}
            >
              <EngineIcon engine={e.key} color={e.iconInk} />
            </div>
            {/* Settled: the kind, and — three across — what it adds. */}
            <div
              style={{
                position: 'absolute',
                left: nx - (tall ? 95 : 170),
                top: slotTop,
                width: tall ? 190 : 340,
                textAlign: 'center',
                transformOrigin: '50% 0',
                opacity: settled * behind,
                transform: `scale(${1 + 0.12 * (1 - settled)})`,
              }}
            >
              <KindPill e={e} size={slotSize} narrow={tall} />
              {!tall && <div style={{ font: `600 20px/26px ${PF}`, color: e.ink, marginTop: 8 }}>{e.add}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Loop: the same wish, three levels, and the families under each ───────────

const LEVELS: ReadonlyArray<{
  key: string;
  label: string;
  fill: number;
  family: Family;
  mood: number;
  energy: number;
  balloon: boolean;
}> = [
  { key: 'generic', label: 'Level 1 · generic AI', fill: 0, family: 1, mood: 0.28, energy: 0.35, balloon: false },
  { key: 'nearby', label: 'Level 2 · knows what’s nearby', fill: 2 / 3, family: 2, mood: 1.0, energy: 1.0, balloon: false },
  { key: 'day', label: 'Level 3 · plans the whole day', fill: 1, family: 3, mood: 1.15, energy: 1.3, balloon: true },
];

const DAY = [
  { time: '9:30', what: 'Gamble Garden · kids’ nature walk', where: 'Palo Alto' },
  { time: '12:00', what: 'Lunch on Castro Street', where: 'Mountain View · 9 min' },
  { time: '2:00', what: 'Ames NASA Visitor Center · launch lab', where: '12 min' },
] as const;

/** Where each level's column stands, and its card's top: three across,
 *  low enough for the acts over the frame's top edge; on a phone, where
 *  they stand above the frame, high enough for the balloons to clear. */
export const levelX = (i: number) => 300 + i * 500;
export const levelTop = (layout: Layout) => (layout === 'tall' ? 80 : 100);

const Added: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, font: `600 17px ${PF}`, color: C.teal }}>
    <Check size={16} />
    {children}
  </div>
);

export const LoopScene: React.FC<{ T: number; start: number; endAt: number; ground: number; top: number }> = ({
  T,
  start,
  endAt,
  ground,
  top,
}) => {
  const out = 1 - draw(T, endAt - 0.6, 0.5);
  const cardW = 460;
  return (
    <div style={{ position: 'absolute', inset: 0, opacity: out }}>
      {LEVELS.map((level, i) => {
        const cx = levelX(i);
        const at = start + 0.9 + i * 0.9;
        const ring = draw(T, at + 0.6, 0.7);
        return (
          <div key={level.key}>
            <div
              style={{
                position: 'absolute',
                left: cx - cardW / 2,
                top,
                width: cardW,
                background: C.white,
                borderRadius: 22,
                boxShadow: `0 14px 28px ${colors.shadowSoft}`,
                padding: '16px 20px 18px',
                boxSizing: 'border-box',
                ...enter(T, at, 0.6, 24),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    font: `600 17px ${SF}`,
                    letterSpacing: 1.3,
                    textTransform: 'uppercase',
                    color: i === 0 ? C.muted : C.teal,
                  }}
                >
                  {level.label}
                </span>
                <svg width="34" height="34" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="26" stroke={C.line} strokeWidth="6" />
                  {ENGINES.map((e, k) => {
                    const share = clamp(level.fill * 3 - k, 0, 1) * ring;
                    return share > 0 ? (
                      <path
                        key={e.key}
                        d={arcPath(32, 32, 26, -90 + k * 120 + 6, -90 + k * 120 + 114)}
                        stroke={e.light}
                        strokeWidth="8"
                        strokeLinecap="round"
                        pathLength={1}
                        strokeDasharray={`${share} 1`}
                      />
                    ) : null;
                  })}
                </svg>
              </div>
              <div
                style={{
                  marginTop: 12,
                  ...WISH_BUBBLE,
                  borderRadius: '14px 14px 3px 14px',
                  padding: '6px 12px',
                  font: `400 18px/25px ${PF}`,
                  display: 'inline-block',
                  float: 'right',
                }}
              >
                {WISH}
              </div>
              <div
                style={{
                  clear: 'both',
                  marginTop: 10,
                  background: C.bubble,
                  borderRadius: '16px 16px 16px 4px',
                  padding: '12px 14px',
                  ...enter(T, at + 0.5, 0.5, 10),
                }}
              >
                {i === 0 && <div style={{ font: `400 22px/30px ${PF}`, color: C.ink }}>{GENERIC}</div>}
                {i === 1 && (
                  <div>
                    <div style={{ font: `500 21px/28px ${PF}`, color: C.ink }}>{NASA}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {NASA_CHIPS.map((chip) => (
                        <Chip key={chip}>{chip}</Chip>
                      ))}
                    </div>
                    <Added>Added to Saturday</Added>
                  </div>
                )}
                {i === 2 && (
                  <div>
                    <div style={{ font: `500 21px/28px ${PF}`, color: C.ink }}>Your Saturday, planned.</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {DAY.map((stop, k) => (
                        <div key={stop.time} style={{ display: 'flex', alignItems: 'baseline', gap: 10, ...enter(T, at + 0.9 + k * 0.3, 0.4, 8) }}>
                          <span style={{ font: `600 17px ${SF}`, color: C.ink, width: 54, flexShrink: 0 }}>{stop.time}</span>
                          <span style={{ font: `400 18px/24px ${PF}`, color: C.ink, flex: 1 }}>{stop.what}</span>
                          <Chip>{stop.where}</Chip>
                        </div>
                      ))}
                    </div>
                    <Added>Added to Saturday · 3 events · drive times included</Added>
                  </div>
                )}
              </div>
            </div>
            <div style={enter(T, at + 0.2, 0.7, 0)}>
              <FamilyGroup
                T={T}
                x={cx}
                ground={ground}
                family={level.family}
                kids={2}
                size={150}
                energy={level.energy}
                mood={level.mood}
                balloon={level.balloon}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
